import { useCallback, useRef } from "react";
import { noteToSemitones, getNoteAtPosition } from "../lib/guitarEngine";
import type { NoteName } from "../lib/guitarEngine";

// ============================================
// Sintetizador simple de guitarra (Web Audio API)
// ============================================

function getFrequency(note: NoteName, octave: number): number {
  const semitone = noteToSemitones(note);
  const midi = (octave + 1) * 12 + semitone;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  durationMs: number = 800,
  volume: number = 0.25
) {
  const now = ctx.currentTime;

  // Oscilador principal (sawtooth + triangle mezclados)
  const osc1 = ctx.createOscillator();
  osc1.type = "sawtooth";
  osc1.frequency.value = frequency;

  const osc2 = ctx.createOscillator();
  osc2.type = "triangle";
  osc2.frequency.value = frequency;

  // Ganancia mezcladora
  const mixGain = ctx.createGain();
  mixGain.gain.value = 0.5;

  // Filtro para suavizar el sawtooth
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = frequency * 4;
  filter.Q.value = 1;

  // Envolvente de amplitud
  const envelope = ctx.createGain();
  envelope.gain.setValueAtTime(0, now);
  envelope.gain.linearRampToValueAtTime(volume, now + 0.02);
  envelope.gain.exponentialRampToValueAtTime(volume * 0.6, now + 0.1);
  envelope.gain.exponentialRampToValueAtTime(0.001, now + durationMs / 1000);

  // Conexiones
  osc1.connect(filter);
  osc2.connect(filter);
  filter.connect(mixGain);
  mixGain.connect(envelope);
  envelope.connect(ctx.destination);

  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + durationMs / 1000 + 0.05);
  osc2.stop(now + durationMs / 1000 + 0.05);

  // Cleanup
  setTimeout(() => {
    try {
      osc1.disconnect();
      osc2.disconnect();
      filter.disconnect();
      mixGain.disconnect();
      envelope.disconnect();
    } catch {}
  }, durationMs + 100);
}

export function useGuitarSynth() {
  const ctxRef = useRef<AudioContext | null>(null);

  const getContext = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
    }
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  /**
   * Toca una única nota por nombre y octava
   */
  const playNote = useCallback(
    (note: NoteName, octave: number, durationMs: number = 800) => {
      try {
        const ctx = getContext();
        const freq = getFrequency(note, octave);
        playTone(ctx, freq, durationMs, 0.25);
      } catch (err) {
        console.error("Error playing note:", err);
      }
    },
    [getContext]
  );

  /**
   * Toca una nota en una posición específica del mástil
   */
  const playFret = useCallback(
    (stringNum: number, fret: number, durationMs: number = 800) => {
      try {
        const ctx = getContext();
        const noteData = getNoteAtPosition(stringNum, fret);
        const freq = getFrequency(noteData.note, noteData.octave);
        playTone(ctx, freq, durationMs, 0.25);
      } catch (err) {
        console.error("Error playing fret:", err);
      }
    },
    [getContext]
  );

  /**
   * Toca múltiples notas simultáneamente (acorde)
   * con un pequeño stagger para simular rasgueo
   */
  const playChord = useCallback(
    (
      notes: { string: number; fret: number; note?: NoteName }[],
      durationMs: number = 1200,
      strumDelayMs: number = 25
    ) => {
      try {
        const ctx = getContext();
        notes.forEach((n, i) => {
          const noteData = getNoteAtPosition(n.string, n.fret);
          const freq = getFrequency(noteData.note, noteData.octave);
          const delay = i * strumDelayMs;
          setTimeout(() => {
            playTone(ctx, freq, durationMs, 0.18);
          }, delay);
        });
      } catch (err) {
        console.error("Error playing chord:", err);
      }
    },
    [getContext]
  );

  return { playNote, playFret, playChord };
}
